<script setup>
// Escolhe uma mídia da biblioteca da conta (kind attachment) ou envia uma
// nova. Filtra pelo tipo do passo (image/document/video/audio) e esconde os
// áudios gerados internamente pela síntese de voz (follow_up_tts:*).
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MediaAssetsAPI from 'dashboard/api/mediaAssets';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mediaType: { type: String, required: true },
  selected: { type: [Number, String], default: null },
});
const emit = defineEmits(['pick']);
const { t } = useI18n();
const assets = ref([]);
const loading = ref(false);
const uploading = ref(false);
const fileInput = ref(null);

const ACCEPT = {
  image: 'image/*',
  audio: 'audio/*',
  video: 'video/mp4,video/3gpp',
  document: '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt',
};

const visible = computed(() =>
  assets.value.filter(
    asset =>
      asset.media_type === props.mediaType &&
      !String(asset.name).startsWith('follow_up_tts:')
  )
);

const load = async () => {
  loading.value = true;
  try {
    const { data } = await MediaAssetsAPI.get({ kind: 'attachment' });
    assets.value = data.payload || [];
  } catch (error) {
    useAlert(t('FUNNEL_TIMELINE.MEDIA_LOAD_ERROR'));
  } finally {
    loading.value = false;
  }
};

const upload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  uploading.value = true;
  try {
    const { data } = await MediaAssetsAPI.upload(file, '', 'attachment');
    assets.value.unshift(data);
    emit('pick', data);
  } catch (error) {
    useAlert(
      error?.response?.data?.error || t('FUNNEL_TIMELINE.MEDIA_UPLOAD_ERROR')
    );
  } finally {
    uploading.value = false;
    if (fileInput.value) fileInput.value.value = '';
  }
};

onMounted(load);
</script>

<template>
  <div class="grid gap-2 rounded-lg border border-n-weak bg-n-solid-1 p-3">
    <div class="flex items-center justify-between gap-2">
      <span class="text-xs uppercase tracking-wide text-n-slate-10">
        {{ t('FUNNEL_TIMELINE.MEDIA_LIBRARY') }}
      </span>
      <label>
        <input
          ref="fileInput"
          type="file"
          class="hidden"
          :accept="ACCEPT[mediaType]"
          @change="upload"
        />
        <Button
          :label="t('FUNNEL_TIMELINE.MEDIA_UPLOAD')"
          icon="i-lucide-upload"
          variant="faded"
          size="sm"
          :is-loading="uploading"
          @click="fileInput?.click()"
        />
      </label>
    </div>
    <p v-if="!loading && !visible.length" class="text-sm text-n-slate-11">
      {{ t('FUNNEL_TIMELINE.MEDIA_EMPTY') }}
    </p>
    <ul v-else class="grid max-h-48 gap-1 overflow-y-auto">
      <li v-for="asset in visible" :key="asset.id">
        <button
          type="button"
          class="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left text-sm hover:bg-n-alpha-2"
          :class="
            asset.id === selected
              ? 'bg-n-brand/10 text-n-brand'
              : 'text-n-slate-12'
          "
          :data-testid="`follow-up-media-${asset.id}`"
          @click="emit('pick', asset)"
        >
          <img
            v-if="asset.media_type === 'image'"
            :src="asset.url"
            alt=""
            class="size-8 rounded object-cover"
          />
          <span
            v-else
            class="grid size-8 place-items-center rounded bg-n-alpha-2"
            :class="
              asset.media_type === 'audio'
                ? 'i-lucide-mic'
                : asset.media_type === 'video'
                  ? 'i-lucide-video'
                  : 'i-lucide-file-text'
            "
          />
          <span class="truncate">{{ asset.name }}</span>
        </button>
      </li>
    </ul>
  </div>
</template>
